import {Icon, IconName} from '@wandb/weave/components/Icon';
import React, {ChangeEvent} from 'react';

import {Analytics} from '../../../../services/analytics';
import {toMongo} from '../../../../util/filters';
import {TriggeredActionType} from '../TriggersTable/utilTypes';
import {getDefaultTriggerDescription} from '../utils';
import {useActionConfigStore} from './ActionStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {useEventStore} from './EventStore';
import {useRunMetricChangeStore} from './RunMetricChangeSection';
import {useRunMetricThresholdStore} from './RunMetricThresholdSection';

/**
 * Page 3 of Create trigger drawer
 * Add name and description for the trigger
 */

export const ACTION_ICONS: Record<TriggeredActionType, IconName> = {
  QueueJobTriggeredAction: 'flash-bolt',
  NotificationTriggeredAction: 'flash-bolt',
  GenericWebhookTriggeredAction: 'webhook',
};

type DrawerPageSummaryProps = {
  triggerName: string;
  triggerDescription: string;
  setTriggerName: (val: string) => void;
  setTriggerDescription: (val: string) => void;
  triggerNameExists: boolean;
};

export const DrawerPageSummary = ({
  triggerName,
  triggerDescription,
  setTriggerName,
  setTriggerDescription,
  triggerNameExists,
}: DrawerPageSummaryProps) => {
  // These update on every state change, BUT! the data in them is not being
  // modified in the summary section, so it won't cause a ton of re-renders.
  const eventConfig = useEventStore();
  const actionConfig = useActionConfigStore();
  const runMetricConfig = useRunMetricThresholdStore();
  const runMetricChangeConfig = useRunMetricChangeStore();
  const notificationConfig = actionConfig.notification;

  const descriptionDict = getDefaultTriggerDescription(
    {
      eventType: eventConfig.eventType,
      triggerScopeType: eventConfig.scopeType,
      entityName: eventConfig.entityName,
      projectName: eventConfig.projectName,
      artifactCollectionName: eventConfig.scopeArtifactCollectionName,
      filters: JSON.stringify(toMongo(eventConfig.filters)),
      runMetricConfig,
      runMetricChangeConfig,
      notificationConfig,
    },
    actionConfig.actionType,
    actionConfig.job.queuedJob?.name ?? '',
    actionConfig.job.queuedJobAlias ?? '',
    actionConfig.webhook.webhook?.name,
    actionConfig.notification.teamSlackIntegration?.channelName
  );
  const iconName =
    ACTION_ICONS[descriptionDict.actionType ?? 'QueueJobTriggeredAction'] ??
    'flash-bolt';

  // Add useEffect to track when page is viewed
  React.useEffect(() => {
    Analytics.track('Automation Creation', {
      type: 'page_view',
      page: 'summary',
      eventType: eventConfig.eventType,
      actionType: actionConfig.actionType,
      entityName: eventConfig.entityName,
    });
    // Only track on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <S.PageLabelRow>Step 3 of 3 - Summary</S.PageLabelRow>
      <S.AutomationDescription>
        <S.AutomationDescriptionBorder />
        <S.AutomationDescriptionInner>
          <S.AutomationDescriptionSection>
            <S.AutomationDescriptionIcon>
              <Icon name="recent-clock" />
            </S.AutomationDescriptionIcon>
            <S.AutomationDescriptionBody>
              <S.AutomationDescriptionTitle>
                {descriptionDict.event.title}
              </S.AutomationDescriptionTitle>
              <S.AutomationDescriptionText>
                {descriptionDict.event.body}
              </S.AutomationDescriptionText>
            </S.AutomationDescriptionBody>
          </S.AutomationDescriptionSection>
          <S.AutomationDescriptionSection>
            <S.AutomationDescriptionIcon>
              <Icon name={iconName} />
            </S.AutomationDescriptionIcon>
            <S.AutomationDescriptionBody>
              <S.AutomationDescriptionTitle>
                {descriptionDict.action.title}
              </S.AutomationDescriptionTitle>
              <S.AutomationDescriptionText>
                {descriptionDict.action.body}
              </S.AutomationDescriptionText>
            </S.AutomationDescriptionBody>
          </S.AutomationDescriptionSection>
        </S.AutomationDescriptionInner>
      </S.AutomationDescription>
      <S.LabelRow>
        <S.Label>Automation name</S.Label>
      </S.LabelRow>
      <S.StyledTextField
        placeholder="Name your automation"
        value={triggerName ?? ''}
        onChange={value => setTriggerName(value)}
        autoFocus={true}
      />
      {triggerNameExists && (
        <S.ErrorText>Automation name already exists.</S.ErrorText>
      )}
      <S.LabelRow>
        <S.Label>Description</S.Label>
      </S.LabelRow>
      <S.StyledTextArea
        value={triggerDescription ?? ''}
        placeholder={`Enter description (optional) \n\n${descriptionDict.stringVal}`}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
          setTriggerDescription(event.target.value)
        }
      />
    </>
  );
};
